import { motion } from 'framer-motion';
import { useAuthor } from '@/hooks/useAuthor';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Trophy, Medal, Award, Sparkles } from 'lucide-react';
import type { Supporter } from '@/types/fundraiser';
import { genUserName } from '@/lib/genUserName';

interface SupportersLeaderboardProps {
  supporters: Supporter[];
  maxDisplay?: number;
}

export function SupportersLeaderboard({ supporters, maxDisplay = 10 }: SupportersLeaderboardProps) {
  const topSupporters = supporters.slice(0, maxDisplay);

  if (supporters.length === 0) {
    return (
      <Card className="border-2 border-orange-500/20 bg-white shadow-xl">
        <CardHeader className="border-b-2 border-orange-500/20 pb-6">
          <CardTitle className="text-3xl font-black text-black flex items-center gap-3">
            <Trophy className="h-8 w-8 text-orange-600" />
            Top Supporters
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-8">
          <div className="text-center py-12">
            <Sparkles className="h-16 w-16 text-orange-400 mx-auto mb-4" />
            <p className="text-lg font-semibold text-gray-700">
              Be the first to support this campaign!
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-2 border-orange-500/20 bg-white shadow-xl">
      <CardHeader className="border-b-2 border-orange-500/20 pb-6">
        <CardTitle className="text-3xl font-black text-black flex items-center gap-3">
          <Trophy className="h-8 w-8 text-orange-600" />
          Top Supporters
        </CardTitle>
        <p className="text-base font-semibold text-orange-600 mt-3">
          {supporters.length} {supporters.length === 1 ? 'supporter has' : 'supporters have'} contributed
        </p>
      </CardHeader>
      <CardContent className="pt-6">
        <div className="space-y-3">
          {topSupporters.map((supporter, index) => (
            <SupporterRow
              key={supporter.pubkey}
              supporter={supporter}
              rank={index + 1}
              index={index}
            />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function SupporterRow({ supporter, rank, index }: { supporter: Supporter; rank: number; index: number }) {
  const { data: author } = useAuthor(supporter.pubkey);
  const metadata = author?.metadata;
  const displayName = metadata?.name || genUserName(supporter.pubkey);
  const avatarUrl = metadata?.picture;

  const getRankIcon = () => {
    if (rank === 1) return <Trophy className="h-6 w-6 text-yellow-500" fill="currentColor" />;
    if (rank === 2) return <Medal className="h-6 w-6 text-gray-400" fill="currentColor" />;
    if (rank === 3) return <Award className="h-6 w-6 text-orange-600" fill="currentColor" />;
    return <span className="text-lg font-black text-orange-600 w-6 text-center">{rank}</span>;
  };

  const getRankStyle = () => {
    if (rank === 1) return 'bg-gradient-to-r from-yellow-50 to-yellow-100 border-yellow-400 shadow-lg shadow-yellow-500/20';
    if (rank === 2) return 'bg-gradient-to-r from-gray-50 to-gray-100 border-gray-300 shadow-lg shadow-gray-400/20';
    if (rank === 3) return 'bg-gradient-to-r from-orange-50 to-orange-100 border-orange-400 shadow-lg shadow-orange-500/20';
    return 'bg-white border-orange-200 hover:border-orange-400 hover:shadow-lg';
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.4, delay: index * 0.05, ease: 'easeOut' }}
      className={`flex items-center gap-5 p-5 rounded-2xl border-2 ${getRankStyle()} transition-all duration-300`}
    >
      <div className="flex items-center justify-center w-10">
        {getRankIcon()}
      </div>

      <Avatar className="h-14 w-14 border-3 border-white ring-2 ring-orange-500">
        <AvatarImage src={avatarUrl} alt={displayName} />
        <AvatarFallback className="bg-orange-500 text-white text-base font-bold">
          {displayName.slice(0, 2).toUpperCase()}
        </AvatarFallback>
      </Avatar>

      <div className="flex-1 min-w-0">
        <p className="text-lg font-bold text-black truncate">{displayName}</p>
        <p className="text-sm font-semibold text-orange-600">
          {supporter.zapCount} {supporter.zapCount === 1 ? 'contribution' : 'contributions'}
        </p>
      </div>

      <div className="text-right">
        <p className="text-2xl font-black text-orange-600">
          {supporter.totalSats.toLocaleString()}
        </p>
        <p className="text-sm font-bold text-gray-600">sats</p>
      </div>
    </motion.div>
  );
}
